import { useCallback, useEffect, useMemo, useState } from "react";

import { useProductModule } from "@/lib/useProductModule";
import { supabase } from "../providers/supabase/supabase";
import { useRoleDataScope } from "../commons/useRoleDataScope";

/**
 * Estado da página "Meu Agente": catálogo (templates + capacidades), as
 * instâncias do usuário e as conexões OAuth ativas.
 *
 * Tudo passa por RLS (migration 89): templates/capacidades são leitura pra
 * qualquer autenticado; instâncias e conexões só do dono. O front não filtra
 * por usuário — o banco filtra.
 */

export type AgentCapability = {
  key: string;
  name: string;
  description: string;
  category: "crm" | "servopa" | "google" | "comms" | "gestao" | "seguros" | "campanhas";
  requires_connection: "google" | "telegram" | null;
  is_write: boolean;
  min_role: string;
  sort_order: number;
};

export type AgentTemplate = {
  slug: string;
  name: string;
  emoji: string | null;
  description: string;
  default_capabilities: string[];
  /** Categorias que este template oferece (migration 96). Ausente = sem filtro,
   *  pra não quebrar em ambiente ainda não migrado. */
  allowed_categories?: string[];
  default_schedule: { briefing_hour?: number; briefing_days?: number[] } | null;
  min_role: string;
  sort_order: number;
  /** Produto em que o template aparece (migration 106). Ausente = ambiente
   *  ainda não migrado; nesse caso não filtramos, pra não sumir com tudo. */
  product_module?: "consortium" | "insurance";
};

export type AgentRun = {
  id: number;
  instance_id: number;
  kind: string;
  run_date: string;
  status: string;
  detail: { text?: string; delivered_telegram?: boolean; error?: string } | null;
  started_at: string;
};

export type AgentInstance = {
  id: number;
  user_id: number;
  template_slug: string;
  display_name: string | null;
  custom_instructions: string | null;
  enabled_capabilities: string[];
  schedule_overrides: { briefing_hour?: number; briefing_days?: number[] } | null;
  channel_chat_id: string | null;
  status: "draft" | "active" | "paused";
};

const ROLE_RANK: Record<string, number> = { user: 0, manager: 1, super_admin: 2 };

export const useAgentHub = () => {
  const { isLoading: scopeLoading, isSuperAdmin, isManager, currentCrmUserId } = useRoleDataScope();
  const [templates, setTemplates] = useState<AgentTemplate[]>([]);
  const [capabilities, setCapabilities] = useState<AgentCapability[]>([]);
  const [instances, setInstances] = useState<AgentInstance[]>([]);
  const [connectedProviders, setConnectedProviders] = useState<Set<string>>(new Set());
  // Último briefing por instância — é assim que o agente entrega quando o
  // Telegram não está configurado (ou o usuário não quis vincular).
  const [lastRunByInstance, setLastRunByInstance] = useState<Map<number, AgentRun>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const myRole = isSuperAdmin ? "super_admin" : isManager ? "manager" : "user";

  const load = useCallback(async () => {
    if (scopeLoading) return;
    setLoading(true);
    setError(null);
    try {
      const [templatesRes, capsRes, instancesRes, connectionsRes] = await Promise.all([
        supabase.from("agent_templates").select("*").eq("is_active", true).order("sort_order"),
        supabase.from("agent_capabilities").select("*").eq("is_active", true).order("sort_order"),
        // RLS já limita ao dono; o eq() é defesa em profundidade pro caso do
        // super admin, que enxerga instância de terceiro.
        currentCrmUserId != null
          ? supabase.from("agent_instances").select("*").eq("user_id", currentCrmUserId)
          : Promise.resolve({ data: [], error: null }),
        currentCrmUserId != null
          ? supabase.from("agent_connections").select("provider").eq("status", "active")
          : Promise.resolve({ data: [], error: null }),
      ]);

      const firstError =
        templatesRes.error || capsRes.error || instancesRes.error || connectionsRes.error;
      if (firstError) throw firstError;

      setTemplates((templatesRes.data ?? []) as AgentTemplate[]);
      setCapabilities((capsRes.data ?? []) as AgentCapability[]);
      const loadedInstances = (instancesRes.data ?? []) as AgentInstance[];
      setInstances(loadedInstances);

      // Briefings recentes das instâncias do usuário (RLS já limita ao dono).
      if (loadedInstances.length > 0) {
        const { data: runs } = await supabase
          .from('agent_runs')
          .select('id, instance_id, kind, run_date, status, detail, started_at')
          .in('instance_id', loadedInstances.map((i) => i.id))
          .eq('kind', 'briefing')
          .eq('status', 'done')
          .order('started_at', { ascending: false })
          .limit(30);
        const porInstancia = new Map<number, AgentRun>();
        for (const run of (runs ?? []) as AgentRun[]) {
          // A query já vem em ordem decrescente: o primeiro de cada é o último.
          if (!porInstancia.has(run.instance_id)) porInstancia.set(run.instance_id, run);
        }
        setLastRunByInstance(porInstancia);
      } else {
        setLastRunByInstance(new Map());
      }
      setConnectedProviders(
        new Set(((connectionsRes.data ?? []) as { provider: string }[]).map((c) => c.provider)),
      );
    } catch (err) {
      console.error("Erro ao carregar agentes:", err);
      setError("Não foi possível carregar seus agentes.");
    } finally {
      setLoading(false);
    }
  }, [scopeLoading, currentCrmUserId]);

  useEffect(() => {
    load();
  }, [load]);

  const produto = useProductModule();

  const allowed = useCallback(
    (minRole: string) => (ROLE_RANK[myRole] ?? 0) >= (ROLE_RANK[minRole] ?? 0),
    [myRole],
  );

  // Dois filtros, e eles respondem perguntas diferentes: min_role diz QUEM
  // pode ativar, product_module diz ONDE o template aparece. O de seguros
  // aparecia no Consórcio — não é só ruído, é oferecer ativar um agente sobre
  // uma carteira que não tem relação com a tela em que a pessoa está.
  //
  // Fora de rota de produto (o hub, em "/"), produto vem null e não filtramos:
  // ali a leitura é a do usuário inteiro.
  const visibleTemplates = useMemo(
    () =>
      templates.filter(
        (t) =>
          allowed(t.min_role) &&
          (produto == null || t.product_module == null || t.product_module === produto),
      ),
    [templates, allowed, produto],
  );

  const visibleCapabilities = useMemo(
    () => capabilities.filter((c) => allowed(c.min_role)),
    [capabilities, allowed],
  );

  const instanceByTemplate = useMemo(
    () => new Map(instances.map((i) => [i.template_slug, i])),
    [instances],
  );

  return {
    loading: loading || scopeLoading,
    error,
    reload: load,
    currentCrmUserId,
    templates: visibleTemplates,
    capabilities: visibleCapabilities,
    instances,
    instanceByTemplate,
    connectedProviders,
    lastRunByInstance,
  };
};

/** Pede o deep-link do Telegram à edge function (o segredo do HMAC fica lá). */
export const fetchTelegramLink = async (instanceId: number): Promise<string> => {
  const { data, error } = await supabase.functions.invoke<{ url: string }>("agent-link", {
    method: "POST",
    body: { instance_id: instanceId },
  });
  if (error || !data?.url) {
    throw new Error("Não foi possível gerar o link do Telegram.");
  }
  return data.url;
};
